import { useContext } from "react";
import UserContext from "../contexts/UserContext.js";

/**
 * @typedef {object} UserContextType
 * @property {object | null} user - The current user object or null if not logged in.
 * @property {string | null} authToken - The authentication token.
 * @property {boolean} loading - True if user data is currently being loaded.
 * @property {Error | null} error - Any error that occurred during authentication or data fetching.
 * @property {function(object | null): void} setUser - Function to manually set the user data.
 * @property {function(): Promise<object | null>} refreshUser - Function to refresh user data from the backend.
 * @property {function(): Promise<string | null>} refreshToken - Function to refresh the authentication token.
 */

// Custom hook for accessing the user context
/**
 * Custom hook for accessing the user context.
 * @returns {UserContextType} The user context value.
 * @throws {Error} If used outside of a UserProvider.
 */
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === null) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
